import { z } from 'zod';
const PUSH_OWNER_KEY = 'botspot-browser-push-owner';
const apiBase = () => process.env.REACT_APP_API_URL || 'api';
export type BrowserPushState = 'available' | 'install' | 'unsupported' | 'blocked';
export function browserPushAvailability(input?: {
  ios: boolean;
  standalone: boolean;
  supported: boolean;
  permission: string;
}): BrowserPushState {
  const state = input ?? {
    ios:
      /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
    standalone:
      Boolean(window.matchMedia?.('(display-mode: standalone)').matches) ||
      navigator.standalone === true,
    supported: 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window,
    permission: 'Notification' in window ? Notification.permission : 'default',
  };
  if (state.ios && !state.standalone) return 'install';
  if (!state.supported) return 'unsupported';
  return state.permission === 'denied' ? 'blocked' : 'available';
}
export async function currentBrowserSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration?.active?.scriptURL.endsWith('/botspot-notifications-sw.js')) return null;
  return registration.pushManager.getSubscription();
}
export async function enableBrowserPush(
  token: string,
  permission: Promise<NotificationPermission> = Notification.requestPermission(),
): Promise<void> {
  // Must be called directly from a click, before a network await on iPhone.
  if ((await permission) !== 'granted')
    throw new Error('Notifications are blocked. Enable them in your browser or device settings.');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const response = await fetch(`${apiBase()}/users/notification-settings/push`, { headers });
  if (!response.ok) throw new Error('Unable to set up browser notifications. Please try again.');
  const config = await response.json();
  if (!config.configured || !config.publicKey)
    throw new Error(
      'Browser notifications are not available yet. Email notifications are available.',
    );
  await navigator.serviceWorker.register('/botspot-notifications-sw.js');
  const registration = await navigator.serviceWorker.ready;
  const previous = await registration.pushManager.getSubscription();
  const encoded = config.publicKey.replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0));
  const subscription =
    previous ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: bytes,
    }));
  try {
    const saved = await fetch(`${apiBase()}/users/notification-settings/push`, {
      method: 'POST',
      headers,
      body: JSON.stringify(subscription.toJSON()),
    });
    if (!saved.ok)
      throw new Error('Unable to link this browser. Disable notifications here and try again.');
    const identity = z.object({ userId: z.string().uuid() }).parse(await saved.json());
    localStorage.setItem(PUSH_OWNER_KEY, identity.userId);
  } catch (error) {
    if (!previous) await subscription.unsubscribe();
    throw error;
  }
}
export async function disableBrowserPush(token?: string | null): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    localStorage.removeItem(PUSH_OWNER_KEY);
    return;
  }
  const subscription = await currentBrowserSubscription();
  try {
    if (token && subscription) {
      const response = await fetch(`${apiBase()}/users/notification-settings/push`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
      if (!response.ok)
        throw new Error('Unable to update browser notifications. Please try again.');
    }
  } finally {
    // Revokes the device capability even if sign-out occurs while offline.
    await subscription?.unsubscribe();
    localStorage.removeItem(PUSH_OWNER_KEY);
    const registration = await navigator.serviceWorker.getRegistration('/');
    registration?.active?.postMessage({ type: 'SIGNED_OUT' });
  }
}

declare global {
  interface Navigator {
    readonly standalone?: boolean;
  }
}

export async function enforceBrowserPushOwner(userId: string | null): Promise<void> {
  const owner = localStorage.getItem(PUSH_OWNER_KEY);
  if (owner && owner !== userId) await disableBrowserPush();
}
